import React from "react";
import { Clock } from "lucide-react";
import { useLocaleFormat } from "../../i18n/useLocaleFormat";

/**
 * QENVA Design System — Guardian Portfolio Panel.
 *
 * Navy hero panel anchoring every primary surface of the QENVA app. The
 * panel is intentionally "dumb" (no fetching, no API logic) — parents pass
 * the data via props and the panel renders the Guardian™ visual language.
 *
 * Five variants tweak the ambient gradient + accent color so the same
 * panel can headline Documents, Dashboard, CrossCheck, Alerts and the
 * Executive Report — all while preserving the Brand Kit identity.
 *
 * Props
 * ──────
 *   kicker          : string   — small caps line above the title, prefixed by
 *                                the green Guardian pulse-dot
 *   title           : string   — optional bold title under the kicker
 *   subtitle        : string   — supporting copy
 *   metrics         : Metric[] — 4 to 6 KPI cells (see Metric shape below)
 *   leadingSlot     : ReactNode— optional rich slot (e.g. Guardian Score gauge)
 *                                rendered LEFT of the metrics grid
 *   status          : { state: "live"|"paused"|"warning", label, hint? }
 *                                small badge top-right of the panel
 *   lastAnalysis    : { iso, label? } — "Analysé il y a 2h" pill (label
 *                                       overrides the relative format)
 *   primaryAction   : { label, onClick, icon? } — pill button top-right
 *   secondaryAction : same shape — second pill, ghost styling
 *   variant         : "documents" | "dashboard" | "crosscheck" | "alerts" | "executive"
 *   loading         : boolean   — show skeleton placeholders
 *   testId          : string    — root data-testid
 *
 * Metric shape
 * ──────
 *   { key, value, label, sub?, tone?, testId? }
 *   value can be a string, number or ReactNode (e.g. <CurrencyValue/>).
 *   tone ∈ "info"|"success"|"danger"|"warning"|"neutral"|"white"
 */

const TONE_TO_CLASS = {
  info:    "text-qenva-blue",
  success: "text-qenva-green",
  danger:  "text-[#FF6B7A]",
  warning: "text-[#FBBF24]",
  neutral: "text-white/70",
  white:   "text-white",
};

const VARIANT_BACKGROUND = {
  // Each variant lays two radial gradients on top of the navy base —
  // tuned per surface so the panel feels native to its page.
  documents:   "radial-gradient(900px 320px at 12% 0%, rgba(22,119,255,0.20), transparent 60%), radial-gradient(700px 280px at 100% 100%, rgba(34,197,94,0.10), transparent 60%)",
  dashboard:   "radial-gradient(960px 340px at 8% 0%, rgba(22,119,255,0.26), transparent 60%), radial-gradient(820px 340px at 100% 110%, rgba(34,197,94,0.16), transparent 60%)",
  crosscheck:  "radial-gradient(900px 340px at 10% 0%, rgba(139,92,246,0.22), transparent 60%), radial-gradient(720px 280px at 100% 100%, rgba(22,119,255,0.14), transparent 60%)",
  alerts:      "radial-gradient(900px 340px at 10% 0%, rgba(251,191,36,0.20), transparent 60%), radial-gradient(700px 280px at 100% 100%, rgba(239,68,68,0.16), transparent 60%)",
  executive:   "radial-gradient(960px 340px at 8% 0%, rgba(34,197,94,0.22), transparent 60%), radial-gradient(720px 320px at 100% 100%, rgba(251,191,36,0.16), transparent 60%)",
};

export default function GuardianPortfolioPanel({
  kicker,
  title,
  subtitle,
  metrics = [],
  leadingSlot,
  status,
  lastAnalysis,
  primaryAction,
  secondaryAction,
  variant = "documents",
  loading = false,
  className = "",
  testId = "guardian-portfolio-panel",
}) {
  const { formatRelative } = useLocaleFormat();
  const gradient = VARIANT_BACKGROUND[variant] || VARIANT_BACKGROUND.documents;
  const hasLeadingSlot = !!leadingSlot;
  const metricsCols = hasLeadingSlot
    ? "grid-cols-2 sm:grid-cols-2 lg:grid-cols-4"
    : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6";

  return (
    <section
      data-testid={testId}
      data-variant={variant}
      className={`relative rounded-2xl bg-qenva-navy text-white overflow-hidden border border-qenva-navy-line ${className}`}
    >
      {/* Ambient radial gradient layer */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.55]"
        style={{ background: gradient }}
      />

      <div className="relative px-6 lg:px-8 py-7">
        {/* Header row : kicker + status badge + actions */}
        <header className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div className="min-w-0 flex-1">
            {kicker && (
              <p className="qenva-kicker" data-testid={`${testId}-kicker`}>
                {kicker}
              </p>
            )}
            {title && (
              <h2 className="font-display font-bold text-white text-[22px] mt-3 tracking-tight">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-[13px] text-white/65 mt-1 max-w-2xl">
                {subtitle}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {status && <StatusBadge {...status} />}
            {lastAnalysis && (
              <LastAnalysisPill
                iso={lastAnalysis.iso}
                label={lastAnalysis.label}
                formatRelative={formatRelative}
                testId={`${testId}-last-analysis`}
              />
            )}
            {secondaryAction && (
              <ActionButton {...secondaryAction} ghost testId={`${testId}-secondary-action`} />
            )}
            {primaryAction && (
              <ActionButton {...primaryAction} testId={`${testId}-primary-action`} />
            )}
          </div>
        </header>

        {/* Body : optional leading slot + metrics grid */}
        {loading ? (
          <div className="h-32 animate-pulse bg-white/5 rounded-lg" />
        ) : (
          <div className={`flex flex-col ${hasLeadingSlot ? "lg:flex-row" : ""} gap-8 lg:gap-10`}>
            {hasLeadingSlot && (
              <div
                className="lg:w-[260px] xl:w-[300px] shrink-0"
                data-testid={`${testId}-leading-slot`}
              >
                {leadingSlot}
              </div>
            )}
            <div
              className={`grid ${metricsCols} gap-x-6 lg:gap-x-8 gap-y-7 flex-1 min-w-0`}
              data-testid={`${testId}-metrics`}
            >
              {metrics.map((m) => (
                <MetricCell key={m.key} metric={m} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── sub-components ─────────────────────────────────────────────────────

function MetricCell({ metric }) {
  const toneClass = TONE_TO_CLASS[metric.tone] || TONE_TO_CLASS.white;
  return (
    <div
      data-testid={metric.testId || `metric-${metric.key}`}
      className="min-w-0"
    >
      <div
        className={`font-display font-bold text-[34px] xl:text-[40px] leading-none tabular-nums tracking-tight truncate ${toneClass}`}
        title={typeof metric.value === "string" || typeof metric.value === "number" ? String(metric.value) : undefined}
      >
        {metric.value ?? "—"}
      </div>
      <p className={`mt-3 text-[13px] font-semibold leading-tight ${toneClass}`}>
        {metric.label}
      </p>
      {metric.sub && (
        <p
          className="text-[12px] text-white/55 mt-1 leading-tight truncate"
          title={metric.sub}
        >
          {metric.sub}
        </p>
      )}
    </div>
  );
}

function StatusBadge({ state = "live", label, hint }) {
  const styles = {
    live:    "bg-qenva-green/10 border-qenva-green/30 text-qenva-green",
    paused:  "bg-white/5 border-white/15 text-white/60",
    warning: "bg-[#FBBF24]/10 border-[#FBBF24]/30 text-[#FBBF24]",
  };
  return (
    <span
      data-testid="guardian-status-badge"
      className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full border text-[11px] uppercase tracking-[0.16em] font-semibold ${styles[state] || styles.live}`}
      title={hint}
    >
      <span className="relative flex w-1.5 h-1.5">
        {state === "live" && (
          <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-current opacity-60" />
        )}
        <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-current" />
      </span>
      {label}
    </span>
  );
}

function LastAnalysisPill({ iso, label, formatRelative, testId }) {
  const display = label || (iso ? formatRelative(iso) : "—");
  return (
    <span
      data-testid={testId}
      title={iso}
      className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white/5 border border-white/15 text-[12px] text-white/80 font-medium whitespace-nowrap"
    >
      <Clock size={12} strokeWidth={2} />
      {display}
    </span>
  );
}

function ActionButton({ label, onClick, to, icon: Icon, ghost = false, testId }) {
  const handleClick = (e) => {
    if (onClick) onClick(e);
    if (to && typeof window !== "undefined") window.location.assign(to);
  };
  const cls = ghost
    ? "inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-white/20 text-white/85 hover:bg-white/10 text-[13px] font-medium transition-colors"
    : "inline-flex items-center gap-1.5 h-9 px-5 rounded-full bg-qenva-blue hover:bg-[#0F66E0] text-white text-[13px] font-semibold shadow-[0_12px_28px_-12px_rgba(22,119,255,0.5)] transition-colors";
  return (
    <button type="button" onClick={handleClick} data-testid={testId} className={cls}>
      {Icon && <Icon size={14} strokeWidth={2.25} />}
      {label}
    </button>
  );
}

/**
 * <GuardianCurrencyValue/> — utility used by callers when a metric value
 * holds money in multiple currencies. Picks the dominant CCY as the
 * primary figure, exposes the rest via tooltip + "+N" pill. NEVER converts
 * FX. Pure helper so the panel itself stays format-agnostic.
 */
export function GuardianCurrencyValue({ totals, fallback = "—" }) {
  const { formatCurrency } = useLocaleFormat();
  const entries = Object.entries(totals || {}).filter(([, v]) => v != null && v > 0);
  if (!entries.length) {
    return <span className="text-white/40 font-normal text-[20px]">{fallback}</span>;
  }
  entries.sort(([, a], [, b]) => b - a);
  const [primaryCur, primaryAmt] = entries[0];
  const tail = entries.slice(1);
  const tailTitle = tail.map(([c, v]) => `${c} ${formatCurrency(v, c)}`).join(" · ");
  return (
    <span
      className="inline-flex items-baseline gap-1.5 max-w-full"
      title={tail.length ? `${formatCurrency(primaryAmt, primaryCur)} · ${tailTitle}` : formatCurrency(primaryAmt, primaryCur)}
    >
      <span className="truncate">{formatCurrency(primaryAmt, primaryCur)}</span>
      {tail.length > 0 && (
        <span className="text-[11px] font-medium text-white/50 px-1.5 py-0.5 rounded border border-white/15 shrink-0">
          +{tail.length}
        </span>
      )}
    </span>
  );
}
